<template>
  <!-- The CMS sign-in screen, with a "forgot password" step that sends the website's reset email
       (admins are ordinary user accounts, so the same reset works). https://trello.com/c/NUkdTYmg -->
  <div class="login-page">
    <div class="login-card">
      <img src="./images/logo.svg" alt="Lavish Locations" class="login-logo"/>

      <template v-if="!forgotPassword">
        <h1 class="login-title">Sign in to the CMS</h1>
        <p v-if="sessionEnded" class="login-note">You've been signed out. Please sign in again.</p>

        <el-form :model="form" label-position="top" @submit.native.prevent="onSubmit">
          <el-form-item label="Email">
            <el-input v-model="form.email" type="email" autocomplete="username" autofocus></el-input>
          </el-form-item>

          <el-form-item label="Password">
            <el-input v-model="form.password" type="password" autocomplete="current-password" show-password></el-input>
          </el-form-item>

          <p v-if="error" class="login-error">{{ error }}</p>

          <el-button class="login-button" type="primary" native-type="submit" :loading="loading">Sign in</el-button>
        </el-form>

        <el-button class="login-link" type="text" @click="openForgotPassword">Forgot your password?</el-button>
      </template>

      <template v-else>
        <h1 class="login-title">Reset your password</h1>

        <template v-if="!resetSent">
          <p class="login-note">Enter your email address and we'll send you a link to set a new password.</p>
          <el-form :model="form" label-position="top" @submit.native.prevent="sendReset">
            <el-form-item label="Email">
              <el-input v-model="form.email" type="email" autocomplete="username" autofocus></el-input>
            </el-form-item>

            <p v-if="error" class="login-error">{{ error }}</p>

            <el-button class="login-button" type="primary" native-type="submit" :loading="loading">Send reset link</el-button>
          </el-form>
        </template>

        <template v-else>
          <p class="login-note">
            If there's an account for <b>{{ form.email }}</b>, we've emailed it a link to set a new password.
            The link works for 1 hour. Check your spam folder if it doesn't arrive.
          </p>
          <p class="login-note">Once you've set a new password, come back here to sign in.</p>
        </template>

        <el-button class="login-link" type="text" icon="el-icon-back" @click="backToSignIn">Back to sign in</el-button>
      </template>
    </div>
  </div>
</template>

<script>
  export default {
    props: {
      // True when a saved sign-in was refused (e.g. it ran out), so we can say why they're here.
      sessionEnded: {type: Boolean, default: false},
    },
    data() {
      return {
        form: {
          email: '',
          password: ''
        },
        loading: false,
        error: '',
        forgotPassword: false,
        resetSent: false,
      }
    },
    methods: {
      onSubmit() {
        this.error = '';
        if (!this.form.email || !this.form.password) {
          this.error = 'Enter your email and password.';
          return;
        }
        this.loading = true;
        this.axios.post(process.env.VUE_APP_ROOT_API + '/api/login/admin', {
          email: this.form.email.trim(),
          password: this.form.password
        })
          .then(response => {
            this.$emit('loginAttempt', response.data);
          })
          .catch(error => {
            const status = error.response && error.response.status;
            this.error = status === 403 || status === 400
              ? "That email and password don't match a CMS account."
              : "Couldn't sign in just now. Please try again.";
          })
          .then(() => {
            this.loading = false;
          });
      },

      openForgotPassword() {
        this.error = '';
        this.resetSent = false;
        this.forgotPassword = true;
      },

      backToSignIn() {
        this.error = '';
        this.forgotPassword = false;
      },

      // Same request as the website's "Forgot password": it always answers the same way, whether
      // or not the address has an account, so this can't be used to find out who has one.
      sendReset() {
        this.error = '';
        const email = this.form.email.trim();
        if (!/^\S+@\S+\.\S+$/.test(email)) {
          this.error = 'Enter a valid email address.';
          return;
        }
        this.loading = true;
        this.axios.get(`${process.env.VUE_APP_ROOT_API}/api/users/requestPasswordReset/${encodeURIComponent(email)}`)
          .then(() => {
            this.resetSent = true;
          })
          .catch(() => {
            this.error = "Couldn't send the email just now. Please try again.";
          })
          .then(() => {
            this.loading = false;
          });
      },
    }
  }
</script>

<style scoped>
  .login-page {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 24px;
    box-sizing: border-box;
    background: linear-gradient(160deg, var(--lav-gray) 0%, #4a454c 100%);
  }

  .login-card {
    width: 100%;
    max-width: 400px;
    padding: 40px 40px 28px;
    box-sizing: border-box;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  }

  .login-logo {
    display: block;
    height: 46px;
    margin: 0 auto 28px;
  }

  .login-title {
    margin: 0 0 20px;
    font-size: 22px;
    text-align: center;
  }

  .login-note {
    margin: 0 0 16px;
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.6;
  }

  .login-error {
    margin: -6px 0 14px;
    color: #F56C6C;
    font-size: 13px;
  }

  .login-card >>> .el-form-item__content > .el-input {
    max-width: none;
  }

  .login-button {
    width: 100%;
    height: 42px;
  }

  .login-link {
    display: block;
    margin: 14px auto 0;
    color: var(--text-muted);
  }

  .login-link:hover {
    color: var(--lav-green-dark);
  }
</style>



// WEBPACK FOOTER //
// src/Login.vue